'use client';

import type {FormEvent} from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import clsx from 'clsx';
import Image from 'next/image';
import {useRouter, useSearchParams} from 'next/navigation';

import {PaginationWithLinks} from '@/modules/article/components/pagination';
import {Banner} from '@/modules/finkn/content-blocks/components/banner';
import {Button} from '@/modules/foundation/components/button';
import {Container} from '@/modules/foundation/components/container';
import {Heading} from '@/modules/foundation/components/typography/heading';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';

import {ALGOLIA_HITS_PER_PAGE, useAlgoliaSearch} from '../useAlgoliaSearch';

import {fetchDocumentsByIds} from './fetchDocumentsByIds';
import {ResultsList} from './results-list';

type Props = {
	heading: string;
};

export const SearchPage = ({heading}: Props) => {
	const router = useRouter();
	const searchParams = useSearchParams();

	const qSearchParams = searchParams.get('q');
	const pageSearchParams = searchParams.get('page') ?? '1';

	const [query, setQuery] = useState<string>(
		typeof qSearchParams === 'string' ? qSearchParams : '',
	);

	const [searchedQuery, setSearchedQuery] = useState<string | null>(null);

	const currentPage = parseInt(pageSearchParams, 10);

	const {
		fetchAlgoliaResults,
		totalHits,
		loadingAlgoliaFetch,
		algoliaIds,
		algoliaError,
	} = useAlgoliaSearch(query, currentPage);

	const triggerSearch = useCallback(async () => {
		await fetchAlgoliaResults();
	}, [fetchAlgoliaResults]);

	// Trigger search when current page changes
	useEffect(() => {
		if (query) {
			triggerSearch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPage]);

	const handleInputChange = useCallback(
		(event: FormEvent<HTMLInputElement>) => {
			setQuery(event.currentTarget.value);
		},
		[],
	);

	const handleSubmit = useCallback(
		async (event: FormEvent<HTMLFormElement>) => {
			event.preventDefault();
			const formData = new FormData(event.currentTarget);
			const formDataQuery = formData.get('query');

			if (typeof formDataQuery !== 'string') {
				return;
			}

			if (formDataQuery.trim().length === 0) {
				return;
			}

			setSearchedQuery(formDataQuery);
			router.push(
				`?q=${encodeURIComponent(formDataQuery.trim())}&page=1`,
			);
			await triggerSearch();
		},
		[router, triggerSearch],
	);

	const {status, data, error, isFetching} = useQuery({
		queryKey: [algoliaIds, currentPage],
		queryFn: () => fetchDocumentsByIds(algoliaIds),
		staleTime: 0,
	});

	// sort data by algoliaIds
	const sortedData = useMemo(() => {
		if (!data) {
			return null;
		}

		const sortedData = data.slice().sort((a, b) => {
			const aIndex = algoliaIds.indexOf(a._id);
			const bIndex = algoliaIds.indexOf(b._id);
			return aIndex - bIndex;
		});

		return sortedData;
	}, [data, algoliaIds]);

	const loadingSearchResult =
		isFetching || status === 'pending' || loadingAlgoliaFetch;

	return (
		<>
			<div
				className={clsx(
					'bg-grey-95',
					'border-b',
					'border-grey-border',
					'py-10',
				)}
			>
				<div className={clsx('mx-auto', 'max-w-[700px]', 'px-6')}>
					<form role="search" onSubmit={handleSubmit}>
						<Paragraph asChild size="sm">
							<label
								htmlFor="query"
								className={clsx(
									'block',
									'font-semibold',
									'mb-2',
								)}
							>
								{heading}
							</label>
						</Paragraph>
						<div className={clsx('flex', 'items-center', 'gap-3')}>
							<input
								id="query"
								name="query"
								type="search"
								value={query}
								onChange={handleInputChange}
								className={clsx(
									'focus-visible:ring-primary-25',
									'bg-white',
									error ? 'border-2' : 'border',
									error
										? 'border-error-35'
										: 'border-grey-25',
									'rounded-lg!',
									'w-full',
									'shadow-[inset_0px_-2px_1px_0px_rgba(0,67,121,0.05),inset_3px_2px_6px_1px_rgba(0,67,121,0.03)]',
								)}
							/>
							<Button
								type="submit"
								className={clsx('py-2!', 'px-8!')}
							>
								Søk
							</Button>
						</div>
					</form>
				</div>
			</div>
			<Container spacingBottom="normal" spacingTop="normal" width="text">
				{(status === 'error' || algoliaError) && (
					<Banner
						severity="error"
						title="Noe gikk galt"
						role="alert"
						options={{fullWidth: true}}
					>
						{error?.message ?? algoliaError}
					</Banner>
				)}

				<ResultsList data={sortedData} loading={loadingSearchResult} />

				{totalHits && totalHits > 0 ? (
					<div className={clsx('mt-8')}>
						<PaginationWithLinks
							page={currentPage}
							pageSize={ALGOLIA_HITS_PER_PAGE}
							totalCount={totalHits}
						/>
					</div>
				) : (
					<>
						{!loadingSearchResult && (
							<div
								className={clsx(
									'flex',
									'gap-8',
									'items-center',
								)}
							>
								<Image
									src="/images/search-heading.svg"
									alt=""
									width={146}
									height={164}
								/>
								{!loadingSearchResult && totalHits === 0 ? (
									<div role="status">
										<Heading level={2} size="lg" spacing>
											0 treff for{' '}
											<strong>{searchedQuery}</strong> med
											valgte søkefilter.
										</Heading>
										<Paragraph size="md">
											Endre søkefilter for å se andre
											treff.
										</Paragraph>
									</div>
								) : (
									<div>
										<Heading size="lg" spacing>
											Søk etter innhold på nettsiden
										</Heading>
										<Paragraph size="md">
											Utforsk alle tjenester, ressurser og
											artikler
										</Paragraph>
									</div>
								)}
							</div>
						)}
					</>
				)}
			</Container>
		</>
	);
};
